import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import BreadcrumbsTsoPq from '@components/Breadcrumbs/BreadcrumbsTsoPq';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import FormFieldTsoPq from '@components/Form/FormFieldTsoPq';
import FormTsoPq from '@components/Form/FormTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Box } from '@mui/material';
import {
  ApiCommonExternalDtoMeasurementDeviceVersionExternalDto,
  useGetMeasurementDeviceByIdHasDataForCurrentVersionQuery,
  useGetMeasurementDeviceByIdHasDataQuery,
  useGetMeasurementDeviceByIdQuery,
  useGetMeasurementDeviceGetPastVersionsByTechnicalNameQuery,
  usePostMeasurementDeviceMutation,
  usePostMeasurementDeviceNewVersionByMeasurementDeviceTechnicalNameMutation,
  usePutMeasurementDeviceMutation,
} from '@redux/apiSlices/measurementDeviceApi';
import { useGetMeasurementDeviceModelGetAllQuery } from '@redux/apiSlices/measurementDeviceModelApi';
import { useAppDispatch } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useRoleCheck } from '@utils/hooks/useRoleCheck';
import {
  baseBooleanSchema,
  commentSchema,
  invalidActiveToDateMessage,
  mandatory,
  measurementDeviceNameSchema,
  pastDateTime,
  pastYear,
  positiveIntegerSchema,
  stringSchema,
} from '@utils/validationSchema';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { object, z } from 'zod';
import {
  StyledCheckboxContainer,
  StyledCheckboxLineWrapper,
  StyledDetail,
  StyledDeviceInfo,
  StyledDeviceInfoLineWrapper,
  StyledFormWrapper,
  StyledLineWrapper,
  StyledMeasurmentDeviceHeader,
  StyledMeasurmentDeviceHistoryContainer,
  StyledMeasurmentDeviceTitle,
  StyledMeasurmentDeviceValue,
  StyledMeasurmentDeviceValueLabel,
  StyledPageWrapper,
  StyledSubtitleSection,
  StyledTitle,
  StyledTitleSection,
  StyledTitleWrapper,
  StyledValuesWrapper,
  StyledVersionSection,
} from './styledManagementSections';

type MeasurementDeviceProps = {
  newMeasurmentDevice?: boolean;
  handleDiscard?: Dispatch<SetStateAction<boolean>>;
  treePath: { id: string; entityId: number; label: string }[] | undefined;
  refetch: any;
  onDeleteConfirm?: () => void;
  onCancel?: () => void;
  deleteTitle?: string;
  deleteText?: string;
  onDiscard?: () => void;
};

const FormContent = ({
  register,
  watch,
  setValue,
  errors,
  hasData,
  setError,
}: {
  register: any;
  watch: any;
  setValue: any;
  errors: any;
  hasData?: boolean;
  setError?: any;
}): ReactNode => {
  const [checkCorrelationAnalysis, setCheckCorrelationAnalysis] = useState<boolean>(true);

  const { t } = useTranslation();
  const { data: measurementDeviceModels } = useGetMeasurementDeviceModelGetAllQuery();

  const selectedMeasurementDeviceModelId = watch('measurementDeviceModelId');
  const selectedMeasurementDeviceModel = useMemo(
    () => measurementDeviceModels?.externalDtos?.find((x) => x.id === selectedMeasurementDeviceModelId),
    [selectedMeasurementDeviceModelId]
  );

  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);

  useEffect(() => {
    setCheckCorrelationAnalysis(watch('isUsedForCorrelationCalculation'));
  }, [watch('isUsedForCorrelationCalculation')]);

  return (
    <StyledFormWrapper>
      <FormFieldTsoPq
        label="management.measurementDevice.designation"
        placeholder="management.measurementDevice.designation"
        name="name"
        type="text"
        customWidth="22.125rem"
        errorMessage="general.error.characterLimit"
        required
        register={register}
        errors={errors}
        disabled={!supportOrUserAllowed}
      />
      <FormFieldTsoPq
        label="management.measurementDevice.technicalIdentifier"
        placeholder="management.measurementDevice.technicalIdentifier"
        name="technicalName"
        type="text"
        customWidth="22.125rem"
        errorMessage="general.error.characterLimit"
        required
        register={register}
        errors={errors}
        disabled={!supportOrUserAllowed || hasData}
      />
      <StyledLineWrapper>
        <FormFieldTsoPq
          label="management.measurementDevice.activationStart"
          placeholder="management.measurementDevice.activationStart"
          name="activeFrom"
          type="datetimepicker"
          customWidth="10.5rem"
          errorMessage="general.error.dateSelect"
          required
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          enableFutureDates={false}
          disabled={!supportOrUserAllowed}
        />
        <FormFieldTsoPq //TODO empty by default. false red highlighting when empty
          label="management.measurementDevice.activationEnd"
          placeholder="management.measurementDevice.activationEnd"
          name="activeTo"
          type="datetimepicker"
          customWidth="10.5rem"
          errorMessage="general.error.dateSelect"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          minDateTime={dayjs(watch('activeFrom'))}
          disabled={!supportOrUserAllowed}
        />
      </StyledLineWrapper>
      <FormFieldTsoPq
        label="management.measurementDevice.manufactureYear"
        placeholder="management.measurementDevice.manufactureYear"
        name="manufacturingYear"
        type="yearpicker"
        customWidth="22.125rem"
        errorMessage="general.error.dateSelect"
        register={register}
        watch={watch}
        setValue={setValue}
        errors={errors}
        disabled={!supportOrUserAllowed}
      />
      <FormFieldTsoPq
        label="management.measurementDevice.deviceTypes"
        placeholder="management.measurementDevice.deviceTypes"
        name="measurementDeviceModelId"
        type="singleSelect"
        customWidth="22.125rem"
        errorMessage="general.error.valueSelect"
        required
        register={register}
        watch={watch}
        setValue={setValue}
        options={
          measurementDeviceModels?.externalDtos?.map((x) => ({
            id: x.id as number,
            label: x.manufacturer + ' ' + x.model,
            value: x.id as number,
          })) ?? []
        }
        errors={errors}
        disabled={!supportOrUserAllowed || hasData}
      />
      {selectedMeasurementDeviceModelId && (
        <StyledDeviceInfo>
          {/* <StyledDeviceInfoLineWrapper>
            <StyledDetail>{t('management.measurementDevice.manufacturer')}</StyledDetail>
            <StyledDetail>
              {
                selectedMeasurementDeviceModel.manufacturer
              }
            </StyledDetail>
          </StyledDeviceInfoLineWrapper> */}
          <StyledDeviceInfoLineWrapper>
            <StyledDetail>{t('management.measurementDevice.noiseLimit')}</StyledDetail>
            <StyledDetail>
              {selectedMeasurementDeviceModel?.noiseThresholdVolt !== undefined
                ? selectedMeasurementDeviceModel?.noiseThresholdVolt +
                'V / ' +
                selectedMeasurementDeviceModel?.noiseThresholdAmpere +
                'A'
                : '- / -'}
            </StyledDetail>
          </StyledDeviceInfoLineWrapper>
          <StyledDeviceInfoLineWrapper>
            <StyledDetail>{t('management.measurementDevice.comment')}</StyledDetail>
            <StyledDetail>{selectedMeasurementDeviceModel?.comment}</StyledDetail>
          </StyledDeviceInfoLineWrapper>
        </StyledDeviceInfo>
      )}
      <StyledCheckboxContainer>
        <TypographyTsoPq typographyType="labelSM" color={tsoPqTheme.palette.customTextColor.color_font_ft_helping_text}>
          {t('management.measurementDevice.includeForAnalyses.title')}
        </TypographyTsoPq>
        <StyledCheckboxLineWrapper>
          <FormFieldTsoPq
            type="checkbox"
            name="isUsedForCorrelationCalculation"
            isChecked={checkCorrelationAnalysis}
            register={register}
            setValue={setValue}
            watch={watch}
            handleChange={() => setCheckCorrelationAnalysis(!checkCorrelationAnalysis)}
          />
          <TypographyTsoPq
            typographyType="labelSM"
            color={tsoPqTheme.palette.customTextColor.color_font_ft_helping_text}
          >
            {t('management.measurementDevice.includeForAnalyses.corellationLabel')}
          </TypographyTsoPq>
        </StyledCheckboxLineWrapper>
        {/* <StyledCheckboxLineWrapper>
          <CheckboxTsoPq />
          <TypographyTsoPq
            typographyType="labelSM"
            color={tsoPqTheme.palette.customTextColor.color_font_ft_helping_text}
          >
            {t('management.measurementDevice.includeForAnalyses.anomalyDetectionLabel')}
          </TypographyTsoPq>
        </StyledCheckboxLineWrapper> */}
      </StyledCheckboxContainer>
      <FormFieldTsoPq
        label="management.measurementDevice.comment"
        placeholder="management.measurementDevice.comment"
        name="comment"
        type="text"
        multiline
        rows={4}
        customWidth="47.125rem"
        errorMessage="general.error.characterLimit"
        register={register}
        errors={errors}
        disabled={!supportOrUserAllowed}
      />
    </StyledFormWrapper>
  );
};

const MeasurementDeviceHistory = ({
  history,
}: {
  history: ApiCommonExternalDtoMeasurementDeviceVersionExternalDto[];
}) => {
  const { t } = useTranslation();

  const { data: measurementDeviceModels } = useGetMeasurementDeviceModelGetAllQuery();

  const getModel = (id: number) => {
    const measurementDeviceModel = measurementDeviceModels?.externalDtos?.find((x) => x.id == id);
    return `${measurementDeviceModel?.manufacturer} ${measurementDeviceModel?.model}`;
  };

  return (
    <>
      {history.map((measurementDeviceVersion) => (
        <StyledMeasurmentDeviceHistoryContainer key={measurementDeviceVersion.id}>
          <StyledMeasurmentDeviceTitle>{measurementDeviceVersion.name}</StyledMeasurmentDeviceTitle>
          <StyledValuesWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentDeviceValueLabel>
                {t('management.measurementPoint.active')}
              </StyledMeasurmentDeviceValueLabel>
              <StyledMeasurmentDeviceValue>
                {measurementDeviceVersion.activeFrom && measurementDeviceVersion.activeTo
                  ? `${format(parseISO(measurementDeviceVersion.activeFrom), 'dd.MM.yyyy HH:mm')} - ${format(parseISO(measurementDeviceVersion.activeTo), 'dd.MM.yyyy HH:mm')}`
                  : ''}
              </StyledMeasurmentDeviceValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentDeviceValueLabel>
                {t('management.measurementDevice.manufactureYear')}
              </StyledMeasurmentDeviceValueLabel>
              <StyledMeasurmentDeviceValue>{measurementDeviceVersion.manufacturingYear}</StyledMeasurmentDeviceValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentDeviceValueLabel>
                {t('management.measurementDevice.deviceTypes')}
              </StyledMeasurmentDeviceValueLabel>
              <StyledMeasurmentDeviceValue>
                {getModel(measurementDeviceVersion.measurementDeviceModelId)}
              </StyledMeasurmentDeviceValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentDeviceValueLabel>
                {t('management.measurementPoint.comment')}
              </StyledMeasurmentDeviceValueLabel>
              <StyledMeasurmentDeviceValue>{measurementDeviceVersion.comment}</StyledMeasurmentDeviceValue>
            </StyledLineWrapper>
          </StyledValuesWrapper>
        </StyledMeasurmentDeviceHistoryContainer>
      ))}
    </>
  );
};

const MeasurmentDevice = ({
  newMeasurmentDevice,
  handleDiscard,
  treePath,
  refetch,
  onDeleteConfirm,
  onCancel,
  deleteTitle,
  deleteText,
  onDiscard,
}: MeasurementDeviceProps) => {
  const dispatch = useAppDispatch();

  const { type, deviceId } = useParams();
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteModalConfirmation, setShowDeleteModalConfirmation] = useState(false);

  const { data: measurementDevice } = useGetMeasurementDeviceByIdQuery(
    type == 'messgeraet' && deviceId ? Number(deviceId) : skipToken
  );
  const { data: history } = useGetMeasurementDeviceGetPastVersionsByTechnicalNameQuery(
    measurementDevice?.externalDto?.technicalName ?? skipToken
  );
  const { data: hasDataForCurrentVersion } = useGetMeasurementDeviceByIdHasDataForCurrentVersionQuery(
    type == 'messgeraet' && deviceId ? Number(deviceId) : skipToken
  );
  const { data: hasDataForAllVersions } = useGetMeasurementDeviceByIdHasDataQuery(
    type == 'messgeraet' && deviceId ? Number(deviceId) : skipToken
  );

  const [addEntity] = usePostMeasurementDeviceMutation();
  const [editEntity] = usePutMeasurementDeviceMutation();
  const [createNewVersionMeasuringDevice] =
    usePostMeasurementDeviceNewVersionByMeasurementDeviceTechnicalNameMutation();

  const activeTo = measurementDevice?.externalDto?.activeTo;
  const isDisabled = !activeTo || new Date(activeTo).getTime() > Date.now();
  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);

  const handleFormSubmit = async (data: any, setError: any) => {
    try {
      if (newMeasurmentDevice) {
        await addEntity({
          externalDto: {
            tsoId: 1,
            measurementPointId: treePath?.findLast((x) => true)?.entityId,
            ...data,
          },
        }).unwrap();

        await refetch();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.measurementDevice.new.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      } else {
        await editEntity({
          externalDto: { ...measurementDevice?.externalDto, ...data },
        }).unwrap();

        await refetch();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.measurementDevice.update.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      }

      handleDiscard && handleDiscard(false);
    } catch (error: any) {
      const requestID = error?.data?.traceId || undefined;
      const isConflict = error?.status === 409;

      const conflictErrorMessage = t('snackbar.measurementDevice.technicalName.conflict');
      const nonConflictErrorMessage = newMeasurmentDevice
        ? t('snackbar.measurementDevice.new.error')
        : t('snackbar.measurementDevice.update.error');

      if (isConflict)
        setError('technicalName', {
          type: 'manual',
          message: conflictErrorMessage,
        });
      else
        dispatch(
          setSnackbar({
            open: true,
            type: 'error',
            textContent: nonConflictErrorMessage,
            copyToClipboard: true,
            requestID,
          })
        );
    }
  };

  const handleCreateNewVersion = async () => {
    if (!measurementDevice?.externalDto?.technicalName) return;

    try {
      await createNewVersionMeasuringDevice(measurementDevice.externalDto.technicalName).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.measurementDevice.newVersion.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );

      await refetch();
    } catch (error: any) {
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.measurementDevice.newVersion.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const validationSchema = object({
    name: stringSchema,
    technicalName: measurementDeviceNameSchema,
    activeFrom: mandatory.and(pastDateTime),
    activeTo: z.preprocess((val) => (val === '' || val == null ? undefined : val), pastDateTime.optional()),
    manufacturingYear: z.preprocess((val) => (val === '' || val == null ? undefined : val), pastYear.optional()),
    measurementDeviceModelId: positiveIntegerSchema,
    comment: commentSchema,
    isUsedForCorrelationCalculation: baseBooleanSchema,
  }).refine(
    ({ activeFrom, activeTo }) => {
      if (!activeFrom || !activeTo) return true;
      return new Date(activeTo) > new Date(activeFrom);
    },
    {
      path: ['activeTo'],
      message: invalidActiveToDateMessage,
    }
  );

  return (
    (deviceId || newMeasurmentDevice) && (
      <StyledPageWrapper newMeasurmentDevice={newMeasurmentDevice}>
        <StyledMeasurmentDeviceHeader>
          <StyledTitleWrapper>
            <StyledTitleSection>
              <StyledTitle>
                {(newMeasurmentDevice && t('management.measurementDevice.create.title')) ||
                  measurementDevice?.externalDto?.name}
              </StyledTitle>
              {!newMeasurmentDevice && (
                <PermissionGate roles={userOrSupportRestriction}>
                  <IconButtonTsoPq
                    sizeVariant={'l'}
                    tooltip={t('management.measurementDevice.measurementDeviceDeleteTitle')}
                    buttonType="transparentPrimaryNoEffect"
                    icon={faTrash}
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowDeleteModal(true);
                    }}
                  />
                </PermissionGate>
              )}
            </StyledTitleSection>
            <StyledVersionSection>
              {!newMeasurmentDevice && (
                <PermissionGate roles={userOrSupportRestriction}>
                  <ButtonTsoPq
                    size="medium"
                    buttonType="filledPrimary"
                    disabled={isDisabled}
                    tooltip={t('version.createNewVersion.tooltip')}
                    textContent={t('version.createNewVersion')}
                    onClick={handleCreateNewVersion}
                  />
                </PermissionGate>
              )}
            </StyledVersionSection>
          </StyledTitleWrapper>
          {!newMeasurmentDevice && (
            <StyledSubtitleSection>{t('management.measurementDevice.subtitle')}</StyledSubtitleSection>
          )}
          <BreadcrumbsTsoPq breadcrumbs={treePath} disableLinks={newMeasurmentDevice} />
        </StyledMeasurmentDeviceHeader>
        <FormTsoPq
          newForm={newMeasurmentDevice}
          defaultValues={{}}
          onSubmit={handleFormSubmit}
          discardChanges={handleDiscard}
          customProps={history}
          values={newMeasurmentDevice ? {} : (measurementDevice?.externalDto ?? {})}
          schema={validationSchema}
          withoutDiscard={!supportOrUserAllowed}
          hideSaveButton={!supportOrUserAllowed}
        >
          {(formProps) => <FormContent {...formProps} hasData={hasDataForCurrentVersion && !newMeasurmentDevice} />}
        </FormTsoPq>
        <Box>
          {!newMeasurmentDevice && (history?.externalDtos?.length ?? 0) > 0 && (
            <>
              <StyledTitle>{t('management.measurementDevice.history.title')}</StyledTitle>
              <MeasurementDeviceHistory history={history?.externalDtos ?? []} />
            </>
          )}
          {showDeleteModal && (
            <ModalDialogTsoPq
              modalState={showDeleteModal}
              title={deleteTitle ?? ''}
              deleteConfirmationModal
              confirmationText={deleteText ?? ''}
              onConfirm={() => {
                !hasDataForAllVersions && onDeleteConfirm?.();
                setShowDeleteModal(false);
                setShowDeleteModalConfirmation(true);
                onDiscard?.();
              }}
              onCancel={() => {
                onCancel?.();
                setShowDeleteModal(false);
              }}
              onClose={() => setShowDeleteModal(false)}
            />
          )}
          {showDeleteModalConfirmation && !!hasDataForAllVersions && (
            <ModalDialogTsoPq
              modalState={showDeleteModalConfirmation}
              title={deleteTitle ?? ''}
              deleteConfirmationModal
              confirmationText={t('management.measurementDevice.measurementDeviceConfirmationDelete') ?? ''}
              onConfirm={() => {
                onDeleteConfirm?.();
                setShowDeleteModalConfirmation(false);
                onDiscard?.();
              }}
              onCancel={() => {
                onCancel?.();
                setShowDeleteModalConfirmation(false);
              }}
              onClose={() => setShowDeleteModalConfirmation(false)}
            />
          )}
        </Box>
      </StyledPageWrapper>
    )
  );
};

export default MeasurmentDevice;
